/**
 * Header behaviour: know when it is pinned, and close what should not be left
 * hanging open.
 *
 * It used to publish the header's height into `--af-header-height` from a
 * ResizeObserver. It no longer does: the stack is two rows of stated height and
 * the token is an expression in `airflux-theme.css`, so the value is right on
 * the first paint instead of arriving a frame late — and the row can condense
 * on pin without the observer feeding that back into the layout it came from.
 *
 * The pinned state comes from the classic sticky-detection trick: observe the
 * sticky wrapper against a viewport inset one pixel from the top. While it sits
 * in normal flow it is fully visible; the moment it pins, its ratio drops below
 * 1 and the header takes its solid fill. No scroll handler, so nothing of ours
 * runs on the scroll thread.
 */
if (!customElements.get('airflux-header')) {
  customElements.define(
    'airflux-header',
    class AirfluxHeader extends HTMLElement {
      connectedCallback() {
        const sticky = this.closest('.shopify-section') || this.parentElement;
        if (sticky) {
          this.pinObserver = new IntersectionObserver(
            ([entry]) => this.classList.toggle('af-header--scrolled', entry.intersectionRatio < 1),
            { threshold: [1], rootMargin: '-1px 0px 0px 0px' }
          );
          this.pinObserver.observe(sticky);
        }

        this.closeOnExit();
        this.closeOnEscape();
        this.closeDrawerOnNavigate();
      }

      disconnectedCallback() {
        this.pinObserver?.disconnect();
        document.removeEventListener('pointerdown', this.onDocPointer);
        document.removeEventListener('focusin', this.onDocFocus);
        this.removeEventListener('keydown', this.onEscape);
      }

      /**
       * Escape closes whatever is open, and the control that opened it takes
       * focus back — otherwise the caret is left in a panel that is no longer
       * on the screen.
       *
       * A `<details>` does not do this by itself. It is worth saying out loud,
       * because a `<details>` DOES answer Escape when it is a popover or a
       * dialog, and the disclosure it is here looks identical from the outside.
       */
      closeOnEscape() {
        this.onEscape = (event) => {
          if (event.key !== 'Escape') return;
          const open = event.target.closest('details[open]');
          if (!open) return;
          event.preventDefault();
          open.open = false;
          open.querySelector('summary')?.focus();
        };
        this.addEventListener('keydown', this.onEscape);
      }

      /**
       * Close the drawer when a link inside it is followed.
       *
       * Every link in it used to leave the page, so the drawer went with the
       * navigation and this was not needed. "Find parts for your car" points at
       * `/#af-finder`, which on the home page is a same-page jump — the browser
       * scrolls, no page loads, and without this the drawer is left sitting over
       * the panel it just scrolled to.
       *
       * `<a[href]>` only: the disclosure summaries inside the drawer are the
       * sub-menu toggles, and closing on those would make a nested menu
       * impossible to open. Not cancelled or delayed either — the jump is the
       * browser's own, and closing a `<details>` in the same task does not
       * interrupt it.
       */
      closeDrawerOnNavigate() {
        const drawer = this.querySelector('.af-drawer');
        if (!drawer) return;

        drawer.addEventListener('click', (event) => {
          if (event.target.closest('a[href]')) drawer.open = false;
        });
      }

      /**
       * The nav dropdowns, the search panel and the drawer are all `<details>`,
       * so they already open, close, take focus and answer Escape without any
       * of this. What a `<details>` does not do is close when you give up on it
       * — click something else, or tab past the last item — and a panel left
       * hanging over the page is the reason people distrust click-to-open menus.
       *
       * The listeners have to be on the document — the whole point is catching
       * what happens OUTSIDE the header — but each one does nothing but read a
       * boolean while everything is shut.
       */
      closeOnExit() {
        this.panels = [...this.querySelectorAll('[data-nav-drop], [data-af-search]')];
        if (!this.panels.length) return;

        const closeOthers = (event) => {
          for (const panel of this.panels) {
            if (panel.open && !panel.contains(event.target)) panel.open = false;
          }
        };

        this.onDocPointer = closeOthers;
        this.onDocFocus = closeOthers;

        // One open at a time: a second panel over the first is two menus and no
        // way to tell which one the keyboard is in.
        for (const panel of this.panels) {
          panel.addEventListener('toggle', () => {
            if (!panel.open) return;
            for (const other of this.panels) if (other !== panel) other.open = false;
          });
        }

        document.addEventListener('pointerdown', this.onDocPointer);
        document.addEventListener('focusin', this.onDocFocus);
      }
    }
  );
}
